import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { fetchGameStatus, rollGameDice, managedEvent } from '../api/games';
import { isUserPitching } from '../helpers/gameHelper';
import { cardFromLineup } from '../helpers/gamePlayHelpers';
import { isMidEventAdvanceRunners } from '../utils/gameUtils';
import { handleGameBusy, handleGameAlreadyFinal } from '../helpers/startGameConflicts';

const BASE_READ_PAUSE_MS = 1800;
const REVEAL_STEP_MS = 400;
const INNING_OVER_MS = 1200;
const PBP_INTERVAL_MS = 500;

const halfInningOf = (ipOuts) => Math.floor((ipOuts ?? 0) / 3);

/**
 * Core game engine: loading, dice rolling, reveal-phase timing,
 * and game data refresh.
 */
const useGameEngine = ({ gameID, currentUser, gameSpeed, managerMode, stopAuto }) => {
    const [game, setGame] = useState(null);
    const [participants, setParticipants] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rolling, setRolling] = useState(false);
    const [revealPhase, setRevealPhase] = useState(null);

    const [currentBatterCard, setCurrentBatterCard] = useState(null);
    const [currentPitcherCard, setCurrentPitcherCard] = useState(null);

    // Display event lags behind the real latest event during reveals —
    // runners/scoreboard read from this so they visually update at the right moment.
    const [displayEvent, setDisplayEvent] = useState(null);
    const [inningOverPopup, setInningOverPopup] = useState(false);

    const [userIsPitching, setUserIsPitching] = useState(false);
    const [displayUserIsPitching, setDisplayUserIsPitching] = useState(false);
    const userIsPitchingRef = useRef(false);
    const eventUserIsPitchingRef = useRef(false);

    const [isMidEventModalOpen, setIsMidEventModalOpen] = useState(false);
    const [isStealModalOpen, setIsStealModalOpen] = useState(false);
    const [showRewardsModal, setShowRewardsModal] = useState(false);
    const [rewardsAwarded, setRewardsAwarded] = useState(null);
    const justFinalized = useRef(false);

    const [managedEventPopup, setManagedEventPopup] = useState(null);
    const managedPopupTimer = useRef(null);

    // Refs for values needed inside executeGameEvent without causing re-creation
    const gameRef = useRef(game);
    gameRef.current = game;
    const managerModeRef = useRef(managerMode);
    managerModeRef.current = managerMode;
    const displayEventRef = useRef(displayEvent);
    displayEventRef.current = displayEvent;

    // ── Refresh game data ──
    const refreshGame = useCallback(async () => {
        const data = await fetchGameStatus(gameID);
        setParticipants(data.GameParticipants);
        setGame(data);
        const latestEvt = data.GameEvents?.[0];
        setDisplayEvent(latestEvt || null);
        setCurrentBatterCard(cardFromLineup(latestEvt?.nextBatterLineup));
        setCurrentPitcherCard(cardFromLineup(latestEvt?.nextPitcherLineup));
        return data;
    }, [gameID]);

    // ── Initial load ──
    useEffect(() => {
        setLoading(true);
        setError(null);
        refreshGame()
            .then(data => {
                if (data.GameEvents?.length > 0) setRevealPhase('done');
            })
            .catch(err => {
                setError('Error fetching game status: ' + err.message);
                toast.error('Error fetching game status: ' + err.message);
            })
            .finally(() => setLoading(false));
    }, [gameID, refreshGame]);

    // ── Track user pitching state ──
    useEffect(() => {
        if (game && currentUser) {
            const pitching = isUserPitching(game, currentUser);
            userIsPitchingRef.current = pitching;
            setUserIsPitching(pitching);
            if (!rolling) setDisplayUserIsPitching(pitching);
            setIsMidEventModalOpen(!managerMode && !!isMidEventAdvanceRunners(game.GameEvents?.[0], pitching));
        }
    }, [game, currentUser, managerMode]);

    // ── Cleanup popup timer ──
    useEffect(() => {
        return () => clearTimeout(managedPopupTimer.current);
    }, []);

    // ── Managed event popup ──
    const showManagedEventPopupFn = useCallback((gameData, prevDescription, wasPitching) => {
        const latestEvt = gameData.GameEvents?.[0];
        if (!latestEvt?.managerActionResult) return;

        const hasOut = latestEvt.managerActionResult.includes('Out');
        const isGoodForUser = hasOut ? wasPitching : !wasPitching;
        const full = latestEvt.resultDescription || '';
        const newText = prevDescription && full.startsWith(prevDescription)
            ? full.slice(prevDescription.length).trim()
            : full;
        const lines = newText.split(/(?<=\.)\s+/).filter(Boolean).map(sentence => {
            const match = sentence.match(/^(.+?)\s+(stole|advanced|was)\b(.*)$/);
            if (match) {
                const name = match[1];
                const action = (match[2] + match[3]).replace(/\.$/, '');
                return { name, action: action.charAt(0).toUpperCase() + action.slice(1) };
            }
            return { name: sentence, action: '' };
        });
        setManagedEventPopup({ lines, isGoodForUser });
        clearTimeout(managedPopupTimer.current);
        const duration = Math.max(1000, BASE_READ_PAUSE_MS * gameSpeed);
        managedPopupTimer.current = setTimeout(() => setManagedEventPopup(null), duration);
    }, [gameSpeed]);

    // ── Update phase: swap display event, cards, detect inning change ──
    const applyUpdate = useCallback((updatedGame, prevDisplayEvent, onAfterReveal) => {
        const latestEvt = updatedGame.GameEvents?.[0];
        setDisplayEvent(latestEvt || null);
        setDisplayUserIsPitching(userIsPitchingRef.current);
        setCurrentBatterCard(cardFromLineup(latestEvt?.nextBatterLineup));
        setCurrentPitcherCard(cardFromLineup(latestEvt?.nextPitcherLineup));

        const prevHalf = halfInningOf(prevDisplayEvent?.ipOuts);
        const newHalf = halfInningOf(latestEvt?.ipOuts);
        const isInningChange = prevDisplayEvent && newHalf !== prevHalf && !updatedGame.isFinal;

        if (isInningChange) {
            setInningOverPopup(true);
            setTimeout(() => {
                setInningOverPopup(false);
                setRevealPhase('done');
                setRolling(false);
                onAfterReveal?.(updatedGame);
            }, INNING_OVER_MS);
        } else {
            setRevealPhase('done');
            setRolling(false);
            onAfterReveal?.(updatedGame);
        }
    }, []);

    // ── Core dice roll execution with reveal timing ──
    // Uses refs for game/managerMode to avoid recreating on every state change.
    const executeGameEvent = useCallback(async (eventFunction, eventArgs, { onError, onAfterReveal } = {}) => {
        setRolling(true);
        setRevealPhase('pending');
        const prevDisplayEvt = displayEventRef.current;

        try {
            const rawGame = await eventFunction(eventArgs);
            const updatedGame = {
                ...rawGame,
                stadium: rawGame.stadium ?? gameRef.current?.stadium,
                cosmetics: rawGame.cosmetics ?? gameRef.current?.cosmetics,
            };
            eventUserIsPitchingRef.current = userIsPitchingRef.current;
            setGame(updatedGame);

            if (updatedGame.forcedNarrative) {
                toast.info(updatedGame.forcedNarrative, { autoClose: 4000 });
            }

            const latestEvt = updatedGame.GameEvents?.[0];
            const isManagedEvent = !!latestEvt?.managedEvent;

            // Auto-manager managed events — show popup immediately
            if (managerModeRef.current && isManagedEvent) {
                setRevealPhase('done');
                setDisplayEvent(latestEvt || null);
                setCurrentBatterCard(cardFromLineup(latestEvt?.nextBatterLineup));
                setCurrentPitcherCard(cardFromLineup(latestEvt?.nextPitcherLineup));
                const prevDesc = gameRef.current?.GameEvents?.[0]?.resultDescription || '';
                showManagedEventPopupFn(updatedGame, prevDesc, eventUserIsPitchingRef.current);
                const popupDuration = Math.max(1000, BASE_READ_PAUSE_MS * gameSpeed);
                setTimeout(() => setRolling(false), popupDuration);
                return;
            }

            // Manual managed events (bunt, IBB) — no dice to reveal, skip to description
            if (isManagedEvent) {
                const readPause = Math.max(1000, BASE_READ_PAUSE_MS * gameSpeed);
                setRevealPhase('description');
                setTimeout(() => {
                    setRevealPhase('update');
                    applyUpdate(updatedGame, prevDisplayEvt, onAfterReveal);
                }, readPause);
                return;
            }

            const step = REVEAL_STEP_MS * gameSpeed;
            const pbpCount = latestEvt?.playByPlay?.length || 1;
            const pbpRevealTime = pbpCount * PBP_INTERVAL_MS * gameSpeed;
            const descriptionPause = Math.max(1000, BASE_READ_PAUSE_MS * gameSpeed) + pbpRevealTime;

            setRevealPhase('batting');
            setTimeout(() => setRevealPhase('pitching'), step);
            setTimeout(() => setRevealPhase('final'), step * 2);
            setTimeout(() => setRevealPhase('description'), step * 3);
            setTimeout(() => {
                setRevealPhase('update');
                applyUpdate(updatedGame, prevDisplayEvt, onAfterReveal);
            }, step * 3 + descriptionPause);
        } catch (err) {
            if (!handleGameBusy(err) && !handleGameAlreadyFinal(err)) {
                toast.error(`Error: ${err.message}`);
            }
            setRolling(false);
            onError?.();
        }
    }, [gameSpeed, showManagedEventPopupFn, applyUpdate]);

    const handleRollDice = useCallback(async (rollArgs) => {
        await executeGameEvent(rollGameDice, rollArgs, { onError: stopAuto, onAfterReveal: rollArgs?.onAfterReveal });
    }, [executeGameEvent, stopAuto]);

    const handleManagedWalk = useCallback(async () => {
        await executeGameEvent(managedEvent, { gameID, event: 'bb' });
    }, [executeGameEvent, gameID]);

    const handleManagedBunt = useCallback(async () => {
        await executeGameEvent(managedEvent, { gameID, event: 'bunt' });
    }, [executeGameEvent, gameID]);

    // ── Modal close handlers ──
    const closeManagedModal = useCallback((closeModal) => async (submitted) => {
        const prevDescription = gameRef.current?.GameEvents?.[0]?.resultDescription || '';
        const wasPitching = userIsPitchingRef.current;
        closeModal(false);
        const data = await refreshGame();
        if (submitted) showManagedEventPopupFn(data, prevDescription, wasPitching);
    }, [refreshGame, showManagedEventPopupFn]);

    const handleStealModalClose = closeManagedModal(setIsStealModalOpen);
    const handleExtraBaseModalClose = closeManagedModal(setIsMidEventModalOpen);

    return {
        game,
        participants,
        loading,
        error,
        rolling,
        revealPhase,
        currentBatterCard,
        currentPitcherCard,
        displayEvent,
        inningOverPopup,
        userIsPitching,
        displayUserIsPitching,
        eventUserIsPitchingRef,
        isMidEventModalOpen,
        isStealModalOpen,
        setIsStealModalOpen,
        showRewardsModal,
        setShowRewardsModal,
        rewardsAwarded,
        setRewardsAwarded,
        justFinalized,
        managedEventPopup,
        handleRollDice,
        handleManagedWalk,
        handleManagedBunt,
        handleStealModalClose,
        handleExtraBaseModalClose,
        refreshGame,
    };
};

export default useGameEngine;
